import Sidebar from '@/components/Home_Sidebar/sidebarView.vue';
import Navbar from '@/components/Home_Navbar/NavbarView.vue';
import { get_token, get_user_id, is_demo_domain } from '@/router/index.js';
import engagenet from '@/assets/create_images/community.png';
import orcaa from '@/assets/create_images/orcaa.png';
import xverdict from '@/assets/create_images/case.png';
import workflows from '@/assets/create_images/project.png';
import analytics from '@/assets/create_images/proba_logo.png';
import complyon from '@/assets/create_images/company.png';

import CreateOrcaa from "@/components/CreateOrcaa/CreateOrcaa.vue";
import CreateProba from "@/components/CreateProba/CreateProba.vue";
import CreateEngagement from "@/components/CreateEngagement/CreateEngagement.vue";
import CreateVerification from "@/components/CreateVerification/CreateVerification.vue";
import CreateWorkflows from "@/components/CreateWorkflows/CreateWorkflows.vue";
import CreateCase from "@/components/CreateCase/CreateCase.vue";
import { show_page_loader, hide_page_loader } from '@/utils/page_loader';
import {
  PLAYGROUND_PLATFORM_KEYS,
  prepare_playground_platform_launch,
  WORKSPACE_PLATFORM_KEYS,
} from '@/utils/playground_platform_launch';

const WORKSPACE_CARD_DEFS = Object.freeze([
  {
    key: "orcaa",
    app_key: "orcaa",
    image_key: "orcaa",
    name: "Orcaa.ai",
    category: "Education & Learning",
    description: "AI-powered learning intelligence platform for personalised education, skills development, and learner insights.",
    cta: "Continue Learning",
    tone: "tone-purple",
    tab: "orcaa",
  },
  {
    key: "workflows",
    app_key: "workflow",
    image_key: "workflows",
    name: "Proba Workflows",
    category: "Automation & Processes",
    description: "AI-powered workflow automation, data mapping, and process building for seamless business operations.",
    cta: "View Workflows",
    tone: "tone-coral",
    tab: "workflows",
  },
  {
    key: "complyon",
    app_key: "complyon",
    image_key: "complyon",
    name: "ComplyOn",
    category: "Company & Compliance",
    description: "Smart stakeholder onboarding, automated compliance checks, and continuous risk monitoring.",
    cta: "Review Now",
    tone: "tone-mint",
    tab: "complyon",
  },
  {
    key: "xverdict",
    app_key: "x-verdict",
    image_key: "xverdict",
    name: "X-Verdict",
    category: "Legal & Adjudication",
    description: "AI-powered legal analysis and case management for efficient adjudication and decision-making.",
    cta: "Explore Cases",
    tone: "tone-sky",
    tab: "xverdict",
  },
  {
    key: "analytics",
    app_key: "proba",
    image_key: "analytics",
    name: "Proba Analytics",
    category: "Data & Insights",
    description: "Real-time dashboards, KPI tracking, and business intelligence for data-driven decisions.",
    cta: "View Analytics",
    tone: "tone-rose",
    tab: "analytics",
  },
  {
    key: "engagenet",
    app_key: "engagenet",
    image_key: "engagenet",
    name: "EngageNet",
    category: "Documentation & Records",
    description: "Centralized workbook for documentation, record keeping, and structured information management.",
    cta: "Open Platform",
    tone: "tone-slate",
    tab: "icworkbook",
  },
]);

export default {
  name: 'HomeView',
  components: {
    Sidebar,
    Navbar,
    CreateOrcaa,
    CreateProba,
    CreateEngagement,
    CreateVerification,
    CreateWorkflows,
    CreateCase
  },
  data: () => ({
    is_workspace_collapsed: false,
    is_graph_collapsed: false,
    card_images: {
      engagenet: engagenet,
      orcaa: orcaa,
      xverdict: xverdict,
      complyon: complyon,
      workflows: workflows,
      analytics: analytics,
    },
    workspace_map: {
      orcaa: "Courses",
      analytics: "Programme",
      complyon: "Company",
      xverdict: "Case",
      workflows: "Project",
      icworkbook: "Community",
    },
    active_tab: "home",

    all_tabs: [
      { label: "Home", value: "home", icon: "bi bi-house", app_key: null  },
      { label: "Orcaa.ai", value: "orcaa", icon: "bi bi-mortarboard-fill", app_key: "orcaa" },
      { label: "Proba Workflows", value: "workflows", icon: "bi bi-diagram-3", app_key: "workflow" },
      { label: "ComplyOn", value: "complyon", icon: "bi bi-building", app_key: "complyon" },
      { label: "X-Verdict", value: "xverdict", icon: "bi bi-balance-scale", app_key: "x-verdict" },
      { label: "Proba Analytics", value: "analytics", icon: "bi bi-bar-chart", app_key: "proba" },
      { label: "EngageNet", value: "icworkbook", icon: "bi bi-journal-bookmark", app_key: "engagenet" },
    ],

    workspace_cards: [],

    allowed_app_keys: [],
    platform_access_loading: false,
  }),

  computed: {
    tabs() {
      return this.all_tabs.filter(
        t => t.app_key === null || this.allowed_app_keys.includes(t.app_key)
      );
    },

    active_component() {
      return ({
        orcaa: "CreateOrcaa",
        analytics: "CreateProba",
        icworkbook: "CreateEngagement",
        xverdict: "CreateCase",
        workflows: "CreateWorkflows",
        complyon: "CreateVerification",
      })[this.active_tab];
    },

    available_workspace_cards() {
      return this.workspace_cards.filter(
        card => this.allowed_app_keys.includes(card.app_key)
      );
    },

    is_demo_domain() {
      return is_demo_domain();
    },

    wrapper_class() {
      return ({
        'page-wrapper-create': true,
      });
    },
  },

  mounted() {
    this.workspace_cards = this.build_workspace_cards();
    this.load_allowed_app_keys();
    this._platform_poll = setInterval(() => {
      this.load_allowed_app_keys();
    }, 15000);
  },

  beforeDestroy() {
    clearInterval(this._platform_poll);
  },

  methods: {
    build_workspace_cards() {
      return WORKSPACE_CARD_DEFS.map(card => ({
        ...card,
        image: Reflect.get(this.card_images, card.image_key),
      }));
    },

    handle_workspace_card_action(card) {
      if (card?.tab) {
        this.active_tab = card.tab;
      }
    },

    async load_allowed_app_keys() {
      this.platform_access_loading = true;

      try {
        if (localStorage.getItem('is_playground_user') === '1') {
          this.allowed_app_keys = [...PLAYGROUND_PLATFORM_KEYS];
          return;
        }
        const token = get_token();
        if (!token) {
          this.allowed_app_keys = [];
          return;
        }

        // Super-admins get everything
        const role = String(localStorage.getItem("role") || "").toLowerCase();
        if (role.includes("super admin")) {
          this.allowed_app_keys = ["orcaa", "proba", "x-verdict", "engagenet", "workflow", "complyon"];
          return;
        }

        const user_id = get_user_id();
        if (!user_id) {
          this.allowed_app_keys = [];
          return;
        }

        const url =
          `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/api/get_user_platform_access_api/?user_id=${user_id}`;

        const res = await fetch(url, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${token}`,
          },
        });

        const payload = await res.json().catch(() => null);

        if (!res.ok || payload?.status !== "success") {
          this.allowed_app_keys = [];
          return;
        }

        const rows = payload?.data || [];
        const enabled_names = rows
          .filter(r => String(r.status || "").toUpperCase() === "ENABLED")
          .map(r => r.platform_name)
          .filter(Boolean);

        this.allowed_app_keys = this.map_platform_names_to_app_keys(enabled_names);

        // If the currently active tab is no longer accessible, fall back to Home
        const active_tab_def = this.all_tabs.find(t => t.value === this.active_tab);
        if (active_tab_def?.app_key && !this.allowed_app_keys.includes(active_tab_def.app_key)) {
          this.active_tab = "home";
        }

      } catch (e) {
        console.error("Failed to load platform access:", e);
        this.allowed_app_keys = [];
      } finally {
        this.platform_access_loading = false;
      }
    },

    normalize_platform_name(name) {
      return String(name || "")
        .trim()
        .toLowerCase()
        .replace(/[._-]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    },

    map_platform_names_to_app_keys(platform_names) {
      const name_to_key = Object.freeze(Object.assign(Object.create(null), {
        "orcaa ai":        "orcaa",
        "orcaa":           "orcaa",
        "proba analytics": "proba",
        "proba":           "proba",
        "analytics":       "proba",
        "complyon":        "complyon",
        "engagenet":       "engagenet",
        "ic workbook":     "engagenet",
        "x verdict":       "x-verdict",
        "xverdict":        "x-verdict",
        "proba workflows": "workflow",
        "workflows":       "workflow",
      }));

      const keys = [];
      (platform_names || []).forEach((name) => {
        const normalized = this.normalize_platform_name(name);
        if (Reflect.has(name_to_key, normalized)) {
          const mapped_key = Reflect.get(name_to_key, normalized);
          if (typeof mapped_key === "string" && !keys.includes(mapped_key)) {
            keys.push(mapped_key);
          }
        }
      });
      return keys;
    },

    launch_and_open(tab) {
      const allowed = Object.keys(this.workspace_map);
      if (!allowed.includes(tab)) {
        console.error("Invalid tab:", tab);
        return;
      }
      const workspace_type = Reflect.get(this.workspace_map, tab);
      this.launch_workspace(workspace_type);
    },

    toggle_collapse(section) {
      if (section === 'workspace') {
        this.is_workspace_collapsed = !this.is_workspace_collapsed;
      } else if (section === 'graph') {
        this.is_graph_collapsed = !this.is_graph_collapsed;
      }
    },

    async launch_workspace(workspace) {
      const platform_key = Reflect.get(WORKSPACE_PLATFORM_KEYS, workspace);
      if (platform_key) {
        const platform_ready = await prepare_playground_platform_launch(platform_key, this.$notyf, this.$router);
        if (!platform_ready) {
          return;
        }
      }

      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/create_workspace/`;
      const is_Demo = this.is_demo_domain;
      const token = get_token();

      if (!is_Demo && !token) {
        return;
      }

      show_page_loader(`Launching ${workspace}...`);

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { "Authorization": `Token ${token}` } : {}),
            'X-Page-URL': window.location.href,
          },
          body: JSON.stringify({ workspace }),
        });
        const data = await response.json();
        if (response.ok) {
          window.location = data.redirect_url;
        } else {
          const msg = data?.message || "Service currently unavailable.";
          this.$notyf.error(msg);
          hide_page_loader();
        }
      } catch (error) {
        this.$notyf.error(`Error: ${String(error)}`);
        hide_page_loader();
      }
    },
  },
};
